import { Injectable, ErrorHandler } from '@angular/core';

declare global {
  interface Window {
    appErrors: any;
    getAppErrors: () => any[];
    clearAppErrors: () => void;
    exportAppErrors: () => string;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ErrorLoggerService implements ErrorHandler {
  
  private errors: any[] = [];
  private maxErrors = 100;

  constructor() {
    this.setupGlobalErrorHandlers();
    this.exposeToWindow();
  }

  /**
   * Expose error logger to window for debugging
   */
  private exposeToWindow(): void {
    (window as any).appErrors = {
      get: () => this.getStoredErrors(),
      clear: () => this.clearErrors(),
      export: () => this.exportErrors()
    };
    
    (window as any).getAppErrors = () => this.getStoredErrors();
    (window as any).clearAppErrors = () => this.clearErrors();
    (window as any).exportAppErrors = () => this.exportErrors();
  }

  /**
   * Setup global error handlers
   */
  private setupGlobalErrorHandlers(): void {
    // Capturar errores no manejados
    window.addEventListener('error', (event) => {
      this.logError({
        type: 'RUNTIME_ERROR',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        timestamp: new Date().toISOString()
      });
    });

    // Capturar promesas rechazadas no manejadas
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        type: 'UNHANDLED_REJECTION',
        reason: event.reason,
        promise: event.promise,
        timestamp: new Date().toISOString()
      });
    });

    // Interceptar console.error
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      this.logError({
        type: 'CONSOLE_ERROR',
        args: args,
        timestamp: new Date().toISOString()
      });
      originalConsoleError.apply(console, args);
    };

    // Interceptar console.warn
    const originalConsoleWarn = console.warn;
    console.warn = (...args: any[]) => {
      this.logError({
        type: 'CONSOLE_WARN',
        args: args,
        timestamp: new Date().toISOString()
      });
      originalConsoleWarn.apply(console, args);
    };
  }

  /**
   * Handle error from Angular ErrorHandler
   */
  handleError(error: Error | any): void {
    this.logError({
      type: 'ANGULAR_ERROR',
      message: error?.message,
      stack: error?.stack,
      error: error,
      timestamp: new Date().toISOString()
    });
    
    // Also log to console for visibility during development
    console.error('ERROR_LOGGED:', error);
  }

  /**
   * Log error to storage
   */
  private logError(errorObj: any): void {
    if (this.errors.length >= this.maxErrors) {
      this.errors.shift();
    }
    this.errors.push(errorObj);
    
    // Save to localStorage
    this.saveToLocalStorage();
  }

  /**
   * Save errors to localStorage
   */
  private saveToLocalStorage(): void {
    try {
      localStorage.setItem('app_errors', JSON.stringify(this.errors));
    } catch (e) {
      console.warn('Could not save errors to localStorage:', e);
    }
  }

  /**
   * Get all logged errors
   */
  getErrors(): any[] {
    return this.errors;
  }

  /**
   * Get errors from localStorage
   */
  getStoredErrors(): any[] {
    try {
      const stored = localStorage.getItem('app_errors');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Clear all errors
   */
  clearErrors(): void {
    this.errors = [];
    localStorage.removeItem('app_errors');
  }

  /**
   * Export errors as JSON for download
   */
  exportErrors(): string {
    return JSON.stringify(this.getStoredErrors(), null, 2);
  }
}
